import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mcGetChains } from '../lib/api';

const KEY = 'taiboku-explorer.chain';
const DEFAULT_CHAIN = 'all';

// Static seed so the selector renders instantly on first paint. The real list
// comes from GET /api/mc/chains (the backend chain registry) — one source of
// truth, so adding a chain on the backend surfaces here with no code change.
export const SEED_CHAINS = [
  { id: 'all', label: 'All Chains', shortLabel: 'ALL', icon: '◉' },
  { id: 'krown', label: 'Krown Network', shortLabel: 'KROWN', icon: 'K', accent: '#f59e0b' },
  { id: 'solana', label: 'Solana', shortLabel: 'SOL', icon: 'S', accent: '#9945ff' },
  { id: 'ethereum', label: 'Ethereum', shortLabel: 'ETH', icon: 'E', accent: '#627eea' },
];

// Back-compat alias for any importer still expecting the old name.
export const SUPPORTED_CHAINS = SEED_CHAINS;

const SHORT_LABEL = {
  ethereum: 'ETH', bsc: 'BNB', polygon: 'POL', arbitrum: 'ARB', optimism: 'OP',
  base: 'BASE', avalanche: 'AVAX', linea: 'LINEA', scroll: 'SCROLL',
  blast: 'BLAST', zksync: 'ZK', mantle: 'MNT', robinhood: 'HOOD', arc: 'ARC',
  krown: 'KROWN', solana: 'SOL',
};

function toOption(c) {
  const short = SHORT_LABEL[c.id] || (c.native || c.id || '?').toUpperCase().slice(0, 5);
  return {
    id: c.id,
    label: c.label || c.id,
    shortLabel: short,
    icon: short.slice(0, 1),
    accent: c.accent,
  };
}

const ChainContext = createContext({
  chain: DEFAULT_CHAIN,
  setChain: () => {},
  chainParam: undefined,
  chainInfo: SEED_CHAINS[0],
  chains: SEED_CHAINS,
});

function readInitialChain(searchParams) {
  // URL param takes precedence (shareable links). Validated against the live
  // registry once it loads, not against a hardcoded list.
  const urlChain = searchParams.get('chain');
  if (urlChain) return urlChain;
  try {
    const saved = localStorage.getItem(KEY);
    if (saved) return saved;
  } catch { /* ignore */ }
  return DEFAULT_CHAIN;
}

export function ChainProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [chain, setChainState] = useState(() => readInitialChain(searchParams));
  const [registry, setRegistry] = useState(null);
  const lastUrlSync = useRef(null);

  // Load the backend chain registry once
  useEffect(() => {
    let cancelled = false;
    mcGetChains()
      .then((d) => { if (!cancelled) setRegistry(d?.chains || []); })
      .catch(() => { /* keep the seed list */ });
    return () => { cancelled = true; };
  }, []);

  const chains = useMemo(() => {
    if (!registry || !registry.length) return SEED_CHAINS;
    const all = SEED_CHAINS[0];
    const opts = registry.map(toOption);
    // Krown first (it's the home chain), then everything else as ordered by the backend
    const krown = opts.find((c) => c.id === 'krown');
    const rest = opts.filter((c) => c.id !== 'krown');
    return [all, ...(krown ? [krown] : []), ...rest];
  }, [registry]);

  const chainIds = useMemo(() => new Set(chains.map((c) => c.id)), [chains]);

  // Persist + broadcast on change
  useEffect(() => {
    try { localStorage.setItem(KEY, chain); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('taiboku:chain-changed', { detail: chain }));
  }, [chain]);

  // Drop an unknown chain (stale localStorage / bad share link) once the real
  // registry has loaded.
  useEffect(() => {
    if (registry && registry.length && !chainIds.has(chain)) setChainState(DEFAULT_CHAIN);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, chainIds]);

  // Sync URL ⇄ state when the URL chain param changes externally
  useEffect(() => {
    const urlChain = searchParams.get('chain');
    if (urlChain === lastUrlSync.current) return;
    if (urlChain && urlChain !== chain) setChainState(urlChain);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setChain = useCallback((next) => {
    setChainState(next);
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_CHAIN) params.delete('chain');
    else params.set('chain', next);
    lastUrlSync.current = next === DEFAULT_CHAIN ? null : next;
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);

  const chainInfo = chains.find((c) => c.id === chain) || chains[0];
  const chainParam = chain === 'all' ? undefined : chain;

  return (
    <ChainContext.Provider value={{ chain, setChain, chainParam, chainInfo, chains }}>
      {children}
    </ChainContext.Provider>
  );
}

export function useChain() {
  return useContext(ChainContext);
}
